/**
 * Extract a single content word from a card front field.
 * If the input is a single word, return it. If a multi-word phrase, drop common stop-words
 * and return the first content word, plus the original phrase as `sourcePhrase`.
 */
const STOP_WORDS: Record<string, Set<string>> = {
  en: new Set(["the","a","an","to","of","in","on","at","and","or","but","is","are","was","were","be","been","being","have","has","had","do","does","did","i","you","he","she","it","we","they","my","your","his","her","its","our","their","this","that","these","those","not","no"]),
  es: new Set(["el","la","los","las","un","una","de","del","y","o","en","a","al","es","son","ser","estar","no","si","yo","tú","él","ella","nosotros","vosotros","ellos","ellas","mi","tu","su","este","esta","ese","esa"]),
  fr: new Set(["le","la","les","un","une","des","de","du","et","ou","en","à","au","aux","est","sont","être","ne","pas","je","tu","il","elle","nous","vous","ils","elles","mon","ton","son","ce","cette","ces"]),
  de: new Set(["der","die","das","den","dem","des","ein","eine","einer","eines","und","oder","in","an","auf","zu","mit","ist","sind","sein","nicht","ich","du","er","sie","es","wir","ihr","mein","dein"]),
  it: new Set(["il","la","i","gli","le","un","una","uno","di","del","della","e","o","in","a","al","alla","è","sono","essere","non","io","tu","lui","lei","noi","voi","loro"]),
  nl: new Set(["de","het","een","van","en","of","in","op","aan","te","is","zijn","niet","ik","jij","hij","zij","wij","jullie","mijn","jouw","haar"]),
  pl: new Set(["i","a","o","w","na","do","z","ze","się","nie","jest","są","być","ja","ty","on","ona","my","wy","oni","one","mój","twój","jego","jej","ten","ta","to"]),
  "pt-BR": new Set(["o","a","os","as","um","uma","de","do","da","e","ou","em","no","na","é","são","ser","estar","não","eu","tu","ele","ela","nós","vós","eles","elas","meu","teu","seu"]),
  tr: new Set(["bir","ve","veya","ile","de","da","ki","mi","mı","mu","mü","ben","sen","o","biz","siz","onlar","bu","şu","değil"]),
  ko: new Set(["은","는","이","가","을","를","에","의","과","와","도","만","나","너","그","우리","저"]),
  ru: new Set(["и","или","но","в","на","по","за","от","до","к","с","со","не","ни","да","я","ты","он","она","оно","мы","вы","они","это","тот","та","то","те","быть","есть","был","была","было","были"]),
  uk: new Set(["і","й","та","або","але","в","у","на","по","за","від","до","к","з","зі","не","ні","я","ти","він","вона","воно","ми","ви","вони","це","той","те","бути","є","був","була","було","були"]),
};

export interface RootWordResult {
  rootWord: string;
  sourcePhrase: string | null;
}

export function extractRootWord(rawText: string, language: string): RootWordResult {
  const cleaned = (rawText ?? "").trim();
  if (!cleaned) return { rootWord: "", sourcePhrase: null };
  const stripped = cleaned.replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, "").trim();
  const tokens = stripped.split(/\s+/u).filter(Boolean);
  if (tokens.length <= 1) {
    return { rootWord: tokens[0]?.toLowerCase() ?? "", sourcePhrase: null };
  }
  const stop = STOP_WORDS[language] ?? new Set<string>();
  const content = tokens.find((t) => !stop.has(t.toLowerCase()));
  return {
    rootWord: (content ?? tokens[0]).toLowerCase().replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, ""),
    sourcePhrase: cleaned,
  };
}
